import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Order, OrderLine, Product, ShortfallReason } from '@prody/shared';
import { ORDER_TRANSITIONS, billableQuantity, computeOrderTotals, lineNet } from '@prody/shared';
import {
  Button, Code, CrateHeader, Dialog, FactList, Field, IconChevronRight,
  IconClose, IconPrint, Input, MoneyText, QuantityText, Section, Select,
  Stamp, Textarea, toast, vars,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useUpdateOrder } from '~/platform/data/queries';
import {
  ORDER_TONE, isTerminalOrderStatus, orderHandlingClass, productName,
} from '~/platform/domain/presentation';
import { orderMeta } from './meta';
import './orders.css';

/* ------------------------------------------------------------------ *
 * The manifest.
 *
 * This is the screen a salesperson lives in while a chef is on the
 * phone, so it answers the three questions that call always contains,
 * in this order: what did they order, what are they actually getting,
 * and what does it come to.
 *
 * The line table shows ordered and picked side by side with the
 * shortfall struck through in chalk — the paper habit it replaces —
 * because "we sent 9 of the 12 you asked for" is the single most
 * common sentence in this trade.
 * ------------------------------------------------------------------ */

const SHORTFALL_REASONS: ShortfallReason[] = [
  'supplier-short', 'out-of-stock', 'quality-rejected', 'price-refused', 'damaged', 'other',
];

export function OrderManifest({
  order,
  productsById,
  onClose,
  onOpenFull,
  embedded = false,
}: {
  order: Order;
  productsById: Map<string, Product>;
  onClose: () => void;
  /** Navigates to the full order page — the desk with every drawer open. */
  onOpenFull?: (() => void) | undefined;
  /** Inside a Sheet the header and close button are already provided. */
  embedded?: boolean;
}) {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const { business, format, can } = useBusiness();
  const updateOrder = useUpdateOrder(business.id);
  const [shortfallLine, setShortfallLine] = useState<OrderLine | null>(null);

  const taxBpsFor = useMemo(() => {
    const rates = new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.bps]));
    return (key: string) => rates.get(key) ?? 0;
  }, [business.settings.taxScheme]);

  const totals = computeOrderTotals(order, taxBpsFor);
  const nextStatuses = ORDER_TRANSITIONS[order.status];
  const canEdit = can('order', 'update');
  const meta = orderMeta(order);

  const advance = (to: (typeof nextStatuses)[number]) => {
    updateOrder.mutate(
      { orderId: order.id, patch: { status: to } },
      {
        onSuccess: () =>
          toast.ok(t('orderPlaced', { number: order.number }), {
            detail: t(`status.${to}`),
          }),
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  return (
    <div className="pd-manifest-body">
      {!embedded ? (
        <CrateHeader
          handlingClass={orderHandlingClass(order, productsById)}
          code={order.number}
          title={order.customerName}
          subtitle={t(`channels.${order.channel}`)}
          stamp={
            <Stamp
              tone={ORDER_TONE[order.status]}
              weight={isTerminalOrderStatus(order.status) ? 'solid' : 'outline'}
            >
              {t(`status.${order.status}`)}
            </Stamp>
          }
          facts={[
            { label: t('deliveryDate'), value: format.date(order.deliveryDate, 'weekday') },
            { label: t('common:labels.window'), value: format.timeWindow(order.deliveryWindow) },
            { label: t('detail.lines'), value: <span className="num">{order.lines.length}</span> },
            {
              label: t('fillRate'),
              value: totals.fillRate === null ? '—' : `${Math.round(totals.fillRate * 100)}%`,
            },
            ...(meta.contactName
              ? [{ label: t('columns.contact'), value: meta.contactName }]
              : []),
            ...(meta.paidMinor !== null
              ? [
                  {
                    label: t('columns.balance'),
                    value: (
                      <span
                        className="num pd-orders__balance"
                        data-settled={totals.gross.minor - meta.paidMinor <= 0 || undefined}
                      >
                        {format.money({
                          minor: totals.gross.minor - meta.paidMinor,
                          currency: order.currency,
                        })}
                      </span>
                    ),
                  },
                ]
              : []),
          ]}
          actions={
            <>
              {onOpenFull ? (
                <Button variant="primary" size="sm" icon={<IconChevronRight />} onClick={onOpenFull}>
                  {t('detail.openFull')}
                </Button>
              ) : null}
              <Button variant="quiet" size="sm" iconOnly aria-label={t('common:actions.print')} icon={<IconPrint />} />
              <Button variant="quiet" size="sm" iconOnly aria-label={t('common:actions.close')} icon={<IconClose />} onClick={onClose} />
            </>
          }
        />
      ) : null}

      <div className="pd-manifest-body__inner">
        {embedded && onOpenFull ? (
          <Button variant="primary" fullWidth icon={<IconChevronRight />} onClick={onOpenFull}>
            {t('detail.openFull')}
          </Button>
        ) : null}
        {/* ---- Status transitions ----
            Only legal moves are offered. The state machine lives in
            @prody/shared, so this list can never drift from the API's. */}
        {canEdit && nextStatuses.length > 0 ? (
          <Section title={t('common:labels.status')}>
            <div className="row" style={{ flexWrap: 'wrap' }}>
              {nextStatuses.map((next, i) => (
                <Button
                  key={next}
                  variant={i === 0 ? 'primary' : next === 'cancelled' ? 'danger' : 'default'}
                  size="sm"
                  busy={updateOrder.isPending}
                  onClick={() => advance(next)}
                >
                  {t(`status.${next}`)}
                </Button>
              ))}
            </div>
          </Section>
        ) : null}

        {/* ---- Lines ---- */}
        <Section
          title={t('detail.lines')}
          actions={
            canEdit ? (
              <Button variant="quiet" size="sm">
                {t('detail.addLine')}
              </Button>
            ) : null
          }
        >
          <table className="pd-lines">
            <thead>
              <tr>
                <th className="stencil" aria-hidden="true" />
                <th className="stencil">{t('common:labels.product')}</th>
                <th className="stencil right">{t('detail.ordered')}</th>
                <th className="stencil right">{t('detail.picked')}</th>
                <th className="stencil right">{t('common:labels.price')}</th>
                <th className="stencil right">{t('common:labels.total')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {order.lines.map((row) => {
                const product = productsById.get(row.productId);
                return (
                  <tr
                    key={row.id}
                    data-hc={product?.handlingClass ?? 'neutral'}
                    data-short={row.shortfall ? true : undefined}
                  >
                    <td className="pd-lines__band">
                      <span className="pd-band" />
                    </td>

                    <td>
                      <div className="pd-lines__name">
                        <span className="truncate">
                          {product ? productName(product, i18n.resolvedLanguage ?? 'en') : row.productName}
                        </span>
                        <Code>{row.sku}</Code>
                      </div>
                      {row.note ? <p className="pd-lines__note">{row.note}</p> : null}
                      {row.shortfall ? (
                        <p className="pd-lines__shortfall">
                          <Stamp tone="alert" size="sm">
                            {t('detail.shortfall')}
                          </Stamp>
                          <span className="num">{format.quantity(row.shortfall.quantity)}</span>
                          <span className="dim">· {t(`shortfallReason.${row.shortfall.reason}`)}</span>
                          {row.shortfall.note ? (
                            <span className="faint">— {row.shortfall.note}</span>
                          ) : null}
                        </p>
                      ) : null}
                      {row.substitution ? (
                        <p className="pd-lines__substitution">
                          <Stamp tone="warn" size="sm">
                            {t('detail.substitute')}
                          </Stamp>
                          <span>
                            {productsById.get(row.substitution.productId)?.sku ?? row.substitution.productId}
                          </span>
                          <span className="num">{format.quantity(row.substitution.quantity)}</span>
                        </p>
                      ) : null}
                    </td>

                    <td className="right">
                      <QuantityText value={row.ordered} />
                    </td>

                    <td className="right">
                      <QuantityText value={row.picked} ordered={row.ordered} />
                    </td>

                    <td className="right">
                      <MoneyText value={row.unitPrice} bare size="sm" />
                      <span className="pd-lines__source" title={t(`detail.priceSource.${row.priceSource}`, { name: '', date: '' })}>
                        {row.priceSource === 'personal' ? '★' : row.priceSource === 'manual' ? '✎' : ''}
                      </span>
                    </td>

                    <td className="right">
                      <MoneyText value={lineNet(row)} bare />
                    </td>

                    <td className="pd-lines__actions">
                      {canEdit && !row.shortfall ? (
                        <Button variant="quiet" size="sm" onClick={() => setShortfallLine(row)}>
                          {t('detail.recordShortfall')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>

        {/* ---- Totals ---- */}
        <Section>
          <dl className="pd-totals">
            <div>
              <dt className="stencil">{t('common:labels.net')}</dt>
              <dd><MoneyText value={totals.net} /></dd>
            </div>
            {totals.discount.minor !== 0 ? (
              <div>
                <dt className="stencil">{t('common:labels.discount')}</dt>
                <dd><MoneyText value={totals.discount} /></dd>
              </div>
            ) : null}
            <div>
              <dt className="stencil">{t('common:labels.tax')}</dt>
              <dd><MoneyText value={totals.tax} /></dd>
            </div>
            <div className="pd-totals__grand">
              <dt className="stencil">{t('common:labels.total')}</dt>
              <dd><MoneyText value={totals.gross} size="lg" /></dd>
            </div>
          </dl>
        </Section>

        {/* ---- Context ---- */}
        <Section title={t('common:labels.notes')}>
          <FactList
            columns={2}
            items={[
              { label: t('channel'), value: t(`channels.${order.channel}`) },
              {
                label: t('common:labels.assignedTo'),
                value: order.assignedToId ?? t('filters.unassigned'),
              },
              ...(order.customerNote
                ? [{ label: t('common:labels.customerNote'), value: order.customerNote, span: true }]
                : []),
              ...(order.internalNote
                ? [{ label: t('common:labels.internalNote'), value: order.internalNote, span: true }]
                : []),
            ]}
          />
        </Section>
      </div>

      {shortfallLine ? (
        <ShortfallDialog
          line={shortfallLine}
          order={order}
          onClose={() => setShortfallLine(null)}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Recording a shortfall is a first-class action, not an edit to a
 * number. It captures how much is missing and why, because the "why"
 * is what the buyer reads tomorrow morning and what the analytics
 * screen groups by.
 * ------------------------------------------------------------------ */

export function ShortfallDialog({
  line,
  order,
  onClose,
}: {
  line: OrderLine;
  order: Order;
  onClose: () => void;
}) {
  const { t } = useTranslation(['orders', 'common']);
  const { business, format } = useBusiness();
  const updateOrder = useUpdateOrder(business.id);

  const maxShort = billableQuantity(line);
  const [quantity, setQuantity] = useState(String(maxShort));
  const [reason, setReason] = useState<ShortfallReason>('supplier-short');
  const [note, setNote] = useState('');

  const parsed = Number(quantity.replace(',', '.'));
  const invalid = !Number.isFinite(parsed) || parsed <= 0 || parsed > maxShort;

  const submit = () => {
    const picked = { value: line.ordered.value - parsed, unit: line.ordered.unit };
    const patch: Partial<Order> = {
      lines: order.lines.map((l) =>
        l.id === line.id
          ? {
              ...l,
              picked,
              shortfall: {
                quantity: { value: parsed, unit: line.ordered.unit },
                reason,
                note: note.trim() || null,
                recordedBy: null,
                recordedAt: new Date().toISOString(),
              },
            }
          : l,
      ),
    };
    updateOrder.mutate(
      { orderId: order.id, patch },
      {
        onSuccess: () => {
          toast.warn(t('detail.shortfallRecorded', { product: line.productName }), {
            detail: `${format.quantity({ value: parsed, unit: line.ordered.unit })} · ${t(`shortfallReason.${reason}`)}`,
          });
          onClose();
        },
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t('detail.recordShortfall')}
      description={line.productName}
      size="sm"
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="primary" disabled={invalid} busy={updateOrder.isPending} onClick={submit}>
            {t('detail.recordShortfall')}
          </Button>
        </>
      }
    >
      <div className="stack" style={vars({ '--gap': 'var(--s-6)' })}>
        <Field
          label={t('detail.shortfall')}
          hint={`${t('common:labels.of')} ${format.quantity(line.ordered)}`}
          error={invalid ? t('common:errors.mustBePositive') : null}
          required
        >
          {({ id, describedBy, invalid: isInvalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={isInvalid}
              numeric
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              suffix={format.quantity(line.ordered).split(' ').at(-1)}
              autoFocus
            />
          )}
        </Field>

        <Field label={t('detail.reason')} required>
          {({ id }) => (
            <Select id={id} value={reason} onChange={(e) => setReason(e.target.value as ShortfallReason)}>
              {SHORTFALL_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`shortfallReason.${r}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t('common:labels.notes')} description={t('detail.reasonHint')}>
          {({ id }) => (
            <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
